import { getServerURL } from './config';
import { withEffectivePermissions } from '../utils/effectivePermissions';
const API_BASE_URL = getServerURL();

if (!API_BASE_URL) {
  console.error('REACT_APP_SERVER_URL environment variable is not set');
}

class AuthService {
  constructor() {
    this.token = localStorage.getItem('authToken');
    this.user = withEffectivePermissions(
      JSON.parse(localStorage.getItem('user') || 'null')
    );
    this.activeCountry = localStorage.getItem('activeCountry') || null;
  }

  normalizeCountry(value) {
    if (!value) return null;
    const normalized = String(value).trim().toUpperCase();
    return normalized || null;
  }

  getAllowedCountries(user = this.user) {
    if (!user) return ['EG'];
    if (user.country_access_mode === 'all') {
      return ['EG', 'UAE'];
    }

    const allowed = Array.isArray(user.allowed_countries)
      ? user.allowed_countries.map((country) => this.normalizeCountry(country)).filter(Boolean)
      : [];

    if (allowed.length > 0) return [...new Set(allowed)];

    const defaultCountry = this.normalizeCountry(user.default_country);
    return [defaultCountry || 'EG'];
  }

  getDefaultCountry(user = this.user) {
    const allowed = this.getAllowedCountries(user);
    const defaultCountry = this.normalizeCountry(user?.default_country);
    if (defaultCountry && allowed.includes(defaultCountry)) {
      return defaultCountry;
    }
    return allowed[0] || 'EG';
  }

  setActiveCountry(country) {
    const normalizedCountry = this.normalizeCountry(country);
    if (!normalizedCountry) return;

    const allowedCountries = this.getAllowedCountries();
    if (!allowedCountries.includes(normalizedCountry)) {
      return;
    }

    this.activeCountry = normalizedCountry;
    localStorage.setItem('activeCountry', normalizedCountry);
  }

  getActiveCountry() {
    const allowedCountries = this.getAllowedCountries();
    const normalizedActive = this.normalizeCountry(this.activeCountry);

    if (normalizedActive && allowedCountries.includes(normalizedActive)) {
      return normalizedActive;
    }

    const fallback = this.getDefaultCountry();
    this.activeCountry = fallback;
    localStorage.setItem('activeCountry', fallback);
    return fallback;
  }

  // Login user
  async login(email, password) {
    try {
      // Clear any stale tokens before attempting a fresh login
      try {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        this.token = null;
        this.user = null;
      } catch (_e) {}

      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle the specific error response structure
        const errorMessage = data.error || data.message || 'Login failed';
        const error = new Error(errorMessage);
        error.error = errorMessage; // Add the error property for the login page
        throw error;
      }

      // Extract token and user from the nested data structure
      const token = data.data?.token;
      const user = withEffectivePermissions(data.data?.user);

      if (!token || !user) {
        throw new Error('Invalid response: missing token or user data');
      }

      // Store token and user data
      this.token = token;
      this.user = user;
      
      localStorage.setItem('authToken', token);
      localStorage.setItem('user', JSON.stringify(user));

      const preferredCountry = this.getDefaultCountry(user);
      this.setActiveCountry(preferredCountry);
      
      return { success: true, user: user };
    } catch (error) {
      console.error('Authentication login failed');
      // Give clearer message on network/CORS failures
      if (error && error.name === 'TypeError') {
        const networkError = new Error('Network error: unable to reach authentication server');
        networkError.error = 'Network error: unable to reach authentication server';
        throw networkError;
      }
      throw error;
    }
  }

  // Get user profile
  async getProfile() {
    if (!this.token) {
      throw new Error('No authentication token');
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/profile`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch profile');
      }

      const result = await response.json();
      let user = withEffectivePermissions(result?.data?.user || null);
      if (user && user._id && !user.id) {
        user.id = user._id;
      }
      this.user = user;
      localStorage.setItem('user', JSON.stringify(user));
      this.getActiveCountry();
      return user;
    } catch (error) {
      console.error('Authentication profile refresh failed');
      throw error;
    }
  }

  // Logout user
  logout() {
    this.token = null;
    this.user = null;
    this.activeCountry = null;
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('activeCountry');
  }

  // Check if user is authenticated
  isAuthenticated() {
    return !!this.token;
  }

  // Get current token
  getToken() {
    return this.token;
  }

  // Get current user
  getUser() {
    return this.user;
  }

  // Check if token is valid by making a request
  async validateToken() {
    if (!this.token) {
      return false;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/profile`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.token}`,
        },
      });

      return response.ok;
    } catch (error) {
      console.error('Authentication token validation failed');
      return false;
    }
  }

  // Initialize auth state from localStorage
  initializeAuth() {
    const token = localStorage.getItem('authToken');
    const user = withEffectivePermissions(
      JSON.parse(localStorage.getItem('user') || 'null')
    );
    
    if (token && user) {
      this.token = token;
      this.user = user;
      localStorage.setItem('user', JSON.stringify(user));
      this.getActiveCountry();
    }
  }

  // Get authenticated headers for API requests
  getAuthHeaders() {
    // Ensure token is synced from storage in case initializeAuth hasn't been called yet in this tab
    if (!this.token) {
      try {
        const stored = localStorage.getItem('authToken');
        if (stored) this.token = stored;
      } catch (_e) {}
    }

    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const activeCountry = this.getActiveCountry();
    if (activeCountry) {
      headers['X-Country'] = activeCountry;
    }

    return headers;
  }

  // Change user password
  async changePassword(newPassword) {
    if (!this.token) {
      throw new Error('No authentication token');
    }

    if (!this.user || !(this.user.id || this.user._id)) {
      throw new Error('User ID not available');
    }
    const userId = this.user.id || this.user._id;

    try {
      const response = await fetch(`${API_BASE_URL}/users/${userId}/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.token}`,
        },
        body: JSON.stringify({
          password: newPassword
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMessage = data.error || data.message || 'Failed to change password';
        throw new Error(errorMessage);
      }

      return data;
    } catch (error) {
      console.error('Change password error:', error);
      throw error;
    }
  }

  // Helper methods for role checking
  hasRole(role) {
    const user = this.getUser();
    return user && user.permissions.includes(role);
  }

  isAdmin() {
    return this.hasRole('admin');
  }

  isPreviewer() {
    return this.hasRole('previewer');
  }

  isDesigner() {
    return this.hasRole('designer');
  }

  isDataEntry() {
    return this.hasRole('data_entry');
  }

  isCustomerService() {
    const user = this.getUser();
    return user && (
      (user.group || '').toLowerCase() === 'customer service' ||
      (user.permissions &&
       user.permissions.includes('view_orders') &&
       user.permissions.includes('view_tickets'))
    );
  }

  // Check if user is Operations
  isOperations() {
    const user = this.getUser();
    return user && (
      (user.group || '').toLowerCase() === 'operations' ||
      (user.permissions &&
       user.permissions.includes('view_orders') &&
       user.permissions.includes('view_tickets') &&
       user.permissions.includes('view_trials') &&
       user.permissions.includes('manage_users'))
    );
  }

  // Check if user has permission to view orders
  canViewOrders() {
    const user = this.getUser();
    return user && (
      (user.group || '').toLowerCase() === 'super admin' ||
      this.isCustomerService() ||
      this.isOperations() ||
      (user.permissions && user.permissions.includes('view_orders'))
    );
  }

  // Check if user has permission to view tickets
  canViewTickets() {
    const user = this.getUser();
    return user && (
      (user.group || '').toLowerCase() === 'super admin' ||
      this.isCustomerService() ||
      this.isOperations() ||
      (user.permissions && user.permissions.includes('view_tickets'))
    );
  }

  // Check if user has permission to manage users
  canManageUsers() {
    const user = this.getUser();
    return user && (
      (user.group || '').toLowerCase() === 'super admin' ||
      this.isOperations() ||
      (user.permissions && user.permissions.includes('manage_users'))
    );
  }

  // Check if user has permission to view trials
  canViewTrials() {
    const user = this.getUser();
    return user && (
      (user.group || '').toLowerCase() === 'super admin' ||
      this.isOperations() ||
      (user.permissions && user.permissions.includes('view_trials'))
    );
  }
}

export const authService = new AuthService();
